<template>
  <div>
    <!-- Loader -->
    <loading-panel :loading="loading"></loading-panel>

    <div class="row">
      <div class="col">
        <card class="no-scrollbar" style="overflow-x: scroll">
          <!-- Tab Navigation -->
          <div class="row mb-3">
            <div class="col">
              <div class="btn-group btn-group-toggle float-left" data-toggle="buttons">
                <label
                  v-for="(tab, index) in tabs"
                  :key="`tab-${index}-${tabKey}`"
                  class="btn btn-sm btn-primary btn-simple"
                  :class="{ active: tab.active }"
                >
                  <input
                    type="radio"
                    @click="switchTab(index)"
                    name="tab-options"
                    autocomplete="off"
                    :checked="tab.active"
                  />
                  <span style="font-size: medium; font-weight: lighter" class="d-none d-sm-block">
                    {{ tab.name }}
                  </span>
                  <span class="d-block d-sm-none">
                    <i :class="tab.icon"></i>
                  </span>
                </label>
              </div>
            </div>
          </div>

          <!-- Filters -->
          <filter-bar
            :filters="filterSchema"
            :initial-value="filterValues"
            :result-count="totalRows"
            :per-page="perPage"
            :per-page-options="perPageOptions"
            saved-filters-key="payments"
            @change="onFiltersChanged"
            @update:perPage="onPerPageChange"
          >
            <template #actions>
              <base-button icon class="btn-link text-white" @click="downloadCSV">
                <i class="tim-icons icon-cloud-download-93"></i>
              </base-button>
            </template>
          </filter-bar>

          <!-- No Results Message -->
          <div class="mt-3" v-if="payments.length === 0 && !loading">
            <div class="font-icon-detail">
              <span style="font-size: 3em">
                <i class="fas fa-exclamation-triangle"></i>
              </span>
              <h4 class="mt-3">{{ noDataMessage }}</h4>
            </div>
          </div>

          <!-- Desktop Table -->
          <div class="row d-none d-sm-block" v-if="payments.length > 0">
            <div class="col">
              <b-table
                hover
                :items="payments"
                :fields="tableFields"
              >
                <template #head(transaction_id)="data">
                  <div style="white-space: nowrap">transaction ID</div>
                </template>
                <template #head(payment_method)="data">
                  <div style="white-space: nowrap">payment method</div>
                </template>
                <template #head(date_processed)="data">
                  <div style="white-space: nowrap">date processed</div>
                </template>

                <template #cell(transaction_id)="row">
                  <span style="white-space: nowrap;">
                    #{{ row.item.order_number }}
                    <el-tooltip content="View transaction" effect="light" :open-delay="300" placement="top">
                      <a
                        v-if="hasPermissions('transactions_view')"
                        :href="`/purchases/transactions/details?id=${row.item.transaction_id}`"
                        target="_blank"
                      >
                        <i class="tim-icons el-icon-top-right"></i>
                      </a>
                    </el-tooltip>
                  </span>
                </template>

                <template #cell(amount)="row">
                  <div style="white-space: nowrap;">
                    {{ formatAmount(row.item.amount) }}
                    <!-- Refund information -->
                    <div v-if="row.item.is_refunded" class="refund-info">
                      <small class="text-warning">
                        <i class="tim-icons icon-refresh-01 ml-1"></i>
                        Refunded: {{ formatAmount(row.item.refunded) }}
                        <span v-if="row.item.is_partially_refunded">(Partial)</span>
                      </small>
                      <div v-if="row.item.remaining > 0" class="remaining-amount">
                        <small class="text-info">
                          Remaining: {{ formatAmount(row.item.remaining) }}
                        </small>
                      </div>
                    </div>
                  </div>
                </template>

                <template #cell(payment_method)="row">
                  <div>
                    <badge :type="getPaymentMethodBadgeType(row.item.type)">
                      {{ capitalizeFirstLetter(row.item.type) }}
                    </badge>
                    <!-- Cardholder name -->
                    <div v-if="row.item.cardholder_name" class="cardholder-info mt-1">
                      <small class="text-white">
                        {{ row.item.cardholder_name }}
                      </small>
                    </div>
                    <!-- Card info -->
                    <div v-if="row.item.card_type || row.item.last_four" class="card-info mt-1">
                      <small class="text-white">
                        {{ row.item.card_display }}
                      </small>
                    </div>
                    <!-- Gateway reference for card payments -->
                    <div v-if="row.item.type === 'card' && row.item.gateway_transaction_id" class="gateway-ref mt-1">
                      <small class="text-white">
                        Gateway reference: {{ row.item.gateway_transaction_id }}
                      </small>
                    </div>
                    <!-- Gift card code for gift card payments -->
                    <div v-if="row.item.type === 'gift_card' && row.item.gateway_transaction_id" class="gift-card-code mt-1">
                      <small class="text-white">
                        Code: {{ getGiftCardCode(row.item.gateway_transaction_id) }}
                      </small>
                    </div>
                  </div>
                </template>

                <template #cell(status)="row">
                  <div>
                    <badge :type="getStatusBadgeType(row.item.status)">
                      {{ row.item.status }}
                    </badge>
                    <!-- Failed message -->
                    <div v-if="row.item.status === 'failed' && row.item.failed_message" class="failed-message mt-1">
                      <small class="text-danger">
                        <i class="tim-icons icon-alert-circle-exc"></i>
                        {{ row.item.failed_message }}
                      </small>
                    </div>
                  </div>
                </template>

                <template #cell(customer)="row">
                  <div style="white-space: nowrap;" v-if="row.item.end_user_id">
                    <div>
                      {{ row.item.customer_first_name }} {{ row.item.customer_last_name }}
                      <el-tooltip content="View customer" effect="light" :open-delay="300" placement="top">
                        <a
                          v-if="hasPermissions('customer_view')"
                          :href="`/customers/details?id=${row.item.end_user_id}`"
                          target="_blank"
                        >
                          <i class="tim-icons el-icon-top-right"></i>
                        </a>
                      </el-tooltip>
                    </div>
                    <small class="text-white">
                      <i class="tim-icons icon-email-85"></i> {{ row.item.customer_email }}
                    </small>
                    <small class="text-white ml-2" v-if="row.item.customer_phone">
                      <i class="tim-icons el-icon-mobile-phone"></i>
                      {{ formatPhone(row.item.country, row.item.customer_phone) }}
                    </small>
                  </div>
                  <span v-else>-</span>
                </template>

                <template #cell(date_processed)="row">
                  <span v-if="!showEditPaymentDate[row.index]" style="white-space: nowrap;">
                    {{ formatDateTime(row.item.processed_at) }}
                    <button v-if="isPaymentDateEditable(row.item)"
                            class="edit-date-btn ml-2"
                            @click="$set(showEditPaymentDate, row.index, true); initEditPaymentDate(row.item, row.index)"
                            title="Edit payment date">
                      <i class="tim-icons icon-pencil"></i>
                    </button>
                  </span>
                  <div v-if="showEditPaymentDate[row.index]">
                    <div class="d-flex align-items-end" style="gap: 8px;">
                      <base-input label="Date" style="width: 170px; margin-bottom: 0;">
                        <el-date-picker
                          v-model="editPaymentDateDate[row.index]"
                          type="date"
                          :format="dateFormatCal"
                          value-format="yyyy-MM-dd"
                          placeholder="Date"
                          :clearable="false"
                          :picker-options="{ disabledDate: (date) => date > new Date(), firstDayOfWeek: first_day_of_week }"
                          @change="$set(editPaymentDateError, row.index, '')"
                        />
                      </base-input>
                      <base-input label="Time" style="width: 140px; margin-bottom: 0;">
                        <el-time-picker
                          v-model="editPaymentDateTime[row.index]"
                          :format="picker_time_format"
                          placeholder="HH:MM"
                          :clearable="false"
                          @change="$set(editPaymentDateError, row.index, '')"
                        />
                      </base-input>
                    </div>
                    <label v-if="editPaymentDateError[row.index]" class="error" style="color: #ec250d;">{{ editPaymentDateError[row.index] }}</label>
                    <div class="mt-2">
                        <base-button size="sm" simple type="primary"
                                     @click="$set(showEditPaymentDate, row.index, false)">
                          Cancel
                        </base-button>
                        <base-button size="sm" type="primary" class="ml-2"
                                     :disabled="!editPaymentDateDate[row.index] || !editPaymentDateTime[row.index] || editPaymentDateLoading"
                                     :loading="editPaymentDateLoading"
                                     @click="updatePaymentDate(row.item, row.index)">
                          Save
                        </base-button>
                    </div>
                  </div>
                </template>

              </b-table>
            </div>
          </div>

          <!-- Mobile Table -->
          <div class="row d-block d-sm-none" v-if="payments.length > 0">
            <div class="col">
              <b-table hover :items="payments" :fields="[{key: 'payment_info', label: `Total results: ${totalRows}`}]">
                <template #cell(payment_info)="row">
                  <div>
                    <!-- Header Row -->
                    <div class="row align-items-center mb-2">
                      <div class="col">
                        <h5 class="mb-0">
                          <badge :type="getPaymentMethodBadgeType(row.item.type)">
                            {{ capitalizeFirstLetter(row.item.type) }}
                          </badge>
                        </h5>
                      </div>
                      <div class="col-auto">
                        <badge :type="getStatusBadgeType(row.item.status)">
                          {{ row.item.status }}
                        </badge>
                      </div>
                    </div>

                    <!-- Card info for mobile -->
                    <div v-if="row.item.card_type || row.item.last_four" class="mb-2">
                      <span class="ml-2">{{ row.item.card_display }}</span>
                    </div>
                    <!-- Gateway reference for card payments on mobile -->
                    <div v-if="row.item.type === 'card' && row.item.gateway_transaction_id" class="mb-2">
                      <strong>Gateway:</strong>
                      <span class="ml-2">{{ row.item.gateway_transaction_id }}</span>
                    </div>
                    <!-- Gift card code for gift card payments on mobile -->
                    <div v-if="row.item.type === 'gift_card' && row.item.gateway_transaction_id" class="mb-2">
                      <strong>Code:</strong>
                      <span class="ml-2">{{ getGiftCardCode(row.item.gateway_transaction_id) }}</span>
                    </div>

                    <!-- Transaction Info -->
                    <div class="mb-2">
                      <strong>Transaction:</strong>
                      <span class="ml-2">#{{ row.item.order_number }}</span>
                      <el-tooltip
                        v-if="hasPermissions('transactions_view')"
                        content="View transaction"
                        effect="light"
                        :open-delay="300"
                        placement="top"
                      >
                        <a
                          :href="`/purchases/transactions/details?id=${row.item.transaction_id}`"
                          target="_blank"
                          class="ml-1"
                        >
                          <i class="tim-icons el-icon-top-right"></i>
                        </a>
                      </el-tooltip>
                    </div>

                    <!-- Amount -->
                    <div class="mb-2">
                      <strong>Amount:</strong>
                      <span class="text-success ml-2">{{ formatAmount(row.item.amount) }}</span>

                      <!-- Refund information for mobile -->
                      <div v-if="row.item.is_refunded" class="refund-info-mobile mt-1">
                        <div class="text-warning">
                          <i class="tim-icons icon-refresh-01"></i>
                          <strong>Refunded:</strong> {{ formatAmount(row.item.refunded) }}
                          <span v-if="row.item.is_partially_refunded">(Partial)</span>
                        </div>
                        <div v-if="row.item.remaining > 0" class="text-info">
                          <strong>Remaining:</strong> {{ formatAmount(row.item.remaining) }}
                        </div>
                      </div>
                    </div>

                    <!-- Failed Message for mobile -->
                    <div v-if="row.item.status === 'failed' && row.item.failed_message" class="mb-2">
                      <strong>Error:</strong>
                      <span class="ml-2 text-danger"><small>{{ row.item.failed_message }}</small></span>
                    </div>

                    <!-- Customer Info -->
                    <div class="mb-2" v-if="row.item.end_user_id">
                      <strong>Customer:</strong>
                      <div class="customer-info mt-1 ml-2">
                        <div>
                          {{ row.item.customer_first_name }} {{ row.item.customer_last_name }}
                          <el-tooltip
                            v-if="hasPermissions('customer_view')"
                            content="View customer"
                            effect="light"
                            :open-delay="300"
                            placement="top"
                          >
                            <a
                              :href="`/customers/details?id=${row.item.end_user_id}`"
                              target="_blank"
                              class="ml-1"
                            >
                              <i class="tim-icons el-icon-top-right"></i>
                            </a>
                          </el-tooltip>
                        </div>
                        <small class="text-white d-block">
                          <i class="tim-icons icon-email-85 mr-1"></i>
                          {{ row.item.customer_email }}
                        </small>
                        <small v-if="row.item.customer_phone" class="text-white d-block">
                          <i class="tim-icons el-icon-mobile-phone mr-1"></i>
                          {{ formatPhone(row.item.country, row.item.customer_phone) }}
                        </small>
                      </div>
                    </div>
                    <div class="mb-2" v-else>
                      <strong>Customer:</strong>
                      <span class="text-white">-</span>
                    </div>

                    <!-- Date Processed -->
                    <div v-if="!showEditPaymentDate[row.index]" class="mb-0">
                      <strong>Date Processed:</strong>
                      <span class="ml-2">{{ formatDateTime(row.item.processed_at) }}</span>
                      <button v-if="isPaymentDateEditable(row.item)"
                              class="edit-date-btn ml-2"
                              @click="$set(showEditPaymentDate, row.index, true); initEditPaymentDate(row.item, row.index)"
                              title="Edit payment date">
                        <i class="tim-icons icon-pencil"></i>
                      </button>
                    </div>

                    <!-- Edit Payment Date -->
                    <div v-if="showEditPaymentDate[row.index]">
                      <div>
                        <div class="row align-items-end">
                          <div class="col-auto">
                            <base-input label="Date" style="width: 170px; margin-bottom: 0;">
                              <el-date-picker
                                v-model="editPaymentDateDate[row.index]"
                                type="date"
                                :format="dateFormatCal"
                                value-format="yyyy-MM-dd"
                                placeholder="Date"
                                :clearable="false"
                                :picker-options="{ disabledDate: (date) => date > new Date(), firstDayOfWeek: first_day_of_week }"
                                @change="$set(editPaymentDateError, row.index, '')"
                              />
                            </base-input>
                          </div>
                          <div class="col-auto">
                            <base-input label="Time" style="width: 140px; margin-bottom: 0;">
                              <el-time-picker
                                v-model="editPaymentDateTime[row.index]"
                                :format="picker_time_format"
                                placeholder="HH:MM"
                                :clearable="false"
                                @change="$set(editPaymentDateError, row.index, '')"
                              />
                            </base-input>
                          </div>
                        </div>
                        <label v-if="editPaymentDateError[row.index]" class="error" style="color: #ec250d;">{{ editPaymentDateError[row.index] }}</label>
                        <div class="mt-2">
                            <base-button size="sm" simple type="primary"
                                         @click="$set(showEditPaymentDate, row.index, false)">
                              Cancel
                            </base-button>
                            <base-button size="sm" type="primary" class="ml-2"
                                         :disabled="!editPaymentDateDate[row.index] || !editPaymentDateTime[row.index] || editPaymentDateLoading"
                                         :loading="editPaymentDateLoading"
                                         @click="updatePaymentDate(row.item, row.index)">
                              Save
                            </base-button>
                        </div>
                      </div>
                    </div>

                    <!-- Additional Details -->
                    <div v-if="row.item.reference_number" class="mt-2 pt-2 border-top">
                      <small class="text-white">
                        <strong>Reference:</strong> {{ row.item.reference_number }}
                      </small>
                    </div>
                  </div>
                </template>
              </b-table>
            </div>
          </div>

          <!-- Pagination -->
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            @input="handlePageChange"
          />
        </card>
      </div>
    </div>
  </div>
</template>

<script>
import currencies from "@/assets/currencies.json";
import { API_LOCATION } from "@/api-config";
import swal from "sweetalert2";
import { Auth } from "aws-amplify";
import { BPagination, BTable } from "bootstrap-vue";
import { Badge } from "@/components";
import { Dropdown, DropdownItem, DropdownMenu, TimePicker } from "element-ui";
import _ from "lodash";
import moment from "moment-timezone";
import { localDateTime, date, time, formatGroupDate, formatGroupTime } from "@/plugins/dates";
import axios from "axios";
import {formatPhoneDisplay} from "@/util/phoneFormatter";

export default {
  name: "PaymentsList",

  components: {
    BPagination,
    BTable,
    Badge,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem,
    [TimePicker.name]: TimePicker,
  },

  data() {
    return {
      // Company settings
      companyId: localStorage.getItem('companyId'),
      companyGroupId: JSON.parse(localStorage.getItem('group')).id,
      timeFormat: JSON.parse(localStorage.getItem('group')).time_format,
      picker_time_format: 'hh:mm A',
      dateFormat: JSON.parse(localStorage.getItem('group')).date_format,
      first_day_of_week: JSON.parse(localStorage.getItem('group')).first_day_of_week,
      numberFormat: JSON.parse(localStorage.getItem('group')).number_format,
      currencySymbol: '$',
      currency: JSON.parse(localStorage.getItem('group')).currency,
      currencyFormat: JSON.parse(localStorage.getItem('group')).currency_format,
      currencies: currencies.currencies,

      // Date format for calendar
      dateFormatCal: '',
      dayOfWeek: '',

      // Core data
      loading: false,
      payments: [],
      showEditPaymentDate: [],
      editPaymentDateDate: [],
      editPaymentDateTime: [],
      editPaymentDateLoading: false,
      editPaymentDateError: [],
      paymentMethods: [
        { method: 'credit_card', name: 'Credit Card' },
        { method: 'paypal', name: 'PayPal' },
        { method: 'bank_transfer', name: 'Bank Transfer' },
        { method: 'apple_pay', name: 'Apple Pay' },
        { method: 'google_pay', name: 'Google Pay' },
        { method: 'cash', name: 'Cash' }
      ],
      paymentStatuses: [
        { status: 'completed', name: 'Completed' },
        { status: 'pending', name: 'Pending' },
        { status: 'failed', name: 'Failed' },
        { status: 'cancelled', name: 'Cancelled' },
        { status: 'refunded', name: 'Refunded' }
      ],

      // Pagination
      currentPage: 1,
      totalRows: 0,
      perPage: 10,
      perPageOptions: [5, 10, 25, 50, 100],

      // Tabs
      activeTabIndex: 0,
      tabKey: 0,

      // Flat filter values for the shared <filter-bar>. Seeded from the URL;
      // saved-views precedence is resolved by FilterBar.
      filterValues: this.buildInitialFilterValues(),
      // FilterBar (saved-views enabled) drives the first fetch via its initial
      // `change`; this guards the page number on that first call.
      didInitialFetch: false,
    }
  },

  computed: {
    tabs() {
      return [
        { name: 'All Payments', icon: 'tim-icons icon-credit-card', active: this.activeTabIndex === 0 },
        { name: 'Successful', icon: 'tim-icons icon-check-2', active: this.activeTabIndex === 1 },
        { name: 'Failed', icon: 'tim-icons icon-simple-remove', active: this.activeTabIndex === 2 },
        { name: 'Pending', icon: 'tim-icons icon-time-alarm', active: this.activeTabIndex === 3 }
      ]
    },

    // Schema for the shared <filter-bar>.
    filterSchema() {
      return [
        { key: 'transaction_id', type: 'text', label: 'Transaction ID', placeholder: 'Transaction ID', operatorLabel: 'contains', primary: true },
        { key: 'amount', type: 'numberrange', label: 'Amount', primary: true },
        { key: 'payment_method', type: 'multiselect', label: 'Payment method', primary: true,
          options: (Array.isArray(this.paymentMethods) ? this.paymentMethods : []).map(m => ({ label: m.name, value: m.method })) },
        { key: 'status', type: 'multiselect', label: 'Status', primary: true,
          options: (Array.isArray(this.paymentStatuses) ? this.paymentStatuses : []).map(s => ({ label: s.name, value: s.status })) },
        { key: 'customer', type: 'text', label: 'Customer', placeholder: 'Customer name or email', operatorLabel: 'contains', primary: true },
        { key: 'date_processed', type: 'daterange', label: 'Date processed', primary: true }
      ]
    },

    tableFields() {
      return [
        { key: 'transaction_id', label: 'Transaction ID' },
        { key: 'amount', label: 'Amount' },
        { key: 'payment_method', label: 'Payment Method' },
        { key: 'status', label: 'Status' },
        { key: 'customer', label: 'Customer' },
        { key: 'date_processed', label: 'Date Processed' }
      ]
    },

    noDataMessage() {
      const messages = {
        0: 'No payments found.',
        1: 'No successful payments found.',
        2: 'No failed payments found.',
        3: 'No pending payments found.'
      }
      return messages[this.activeTabIndex] || 'No payments found.'
    }
  },

  mounted() {
    // Set up currency symbol
    this.currencySymbol = _.find(this.currencies, { code: this.currency }).symbol

    // Set up date format for calendar
    this.dateFormatCal = this.dateFormat.replace(/Y/g, 'y').replace(/D/g, 'd')
    if (JSON.parse(localStorage.getItem('group')).show_day_of_week) {
      this.dayOfWeek = 'ddd, '
    }
    if (this.timeFormat === 'HH:mm') {
      this.picker_time_format = 'HH:mm'
    }

    // Seed pagination/tab from the URL. Default when the query omits count/page
    // so Number(undefined) => NaN never builds a bad fetch URL.
    this.perPage = Number(this.$route.query.count) || 10
    this.currentPage = Number(this.$route.query.page) || 1
    this.activeTabIndex = Number(this.$route.query.tab) || 0

    // FilterBar owns the initial applied filter state (saved-views precedence) and
    // emits one initial `change` that drives the first fetch — see onFiltersChanged.
    // Show the loading overlay until that lands.
    this.loading = true

    // Payment-method options for the filter chip (labels only).
    this.fetchPaymentMethods()
    this.fetchPaymentStatuses()
  },

  methods: {
    // Seed the flat filter values from the URL query.
    buildInitialFilterValues() {
      const q = this.$route.query || {}
      return {
        transaction_id: q.transaction_id || '',
        customer: q.customer || '',
        amount: {
          from: q.amount_from || '',
          to: q.amount_to || '',
        },
        payment_method: q.payment_method ? String(q.payment_method).split(',') : [],
        status: q.status ? String(q.status).split(',') : [],
        date_processed: {
          mode: 'range',
          days: '',
          from: q.date_processed_from || '',
          to: q.date_processed_to || '',
        },
      }
    },

    // Derive plain yyyy-MM-dd from/to from the current date_processed filter value,
    // matching the params the backend already expects.
    computeDateProcessedRange() {
      const d = this.filterValues.date_processed || {}
      let from = ''
      let to = ''
      if (d.mode === 'days' && d.days !== '' && d.days != null) {
        const today = new Date()
        const start = new Date()
        start.setDate(start.getDate() - Number(d.days))
        from = start.getFullYear() + '-' + ('0' + (start.getMonth() + 1)).slice(-2) + '-' + ('0' + start.getDate()).slice(-2)
        to = today.getFullYear() + '-' + ('0' + (today.getMonth() + 1)).slice(-2) + '-' + ('0' + today.getDate()).slice(-2)
      } else if (d.mode === 'range') {
        from = d.from || ''
        to = d.to || ''
      }
      return { from, to }
    },

    formatPhone(country, phone) {
      return formatPhoneDisplay(phone, country || 'US');
    },
    getGiftCardCode(code){
      if(!code){
        return
      }
      if(isNaN(code)){
        return code
      }
      let c = code.match(/.{1,4}/g);
      return c.join(' ')
    },
    capitalizeFirstLetter(string){
      if (!string) {
        return string;
      }
      // Special case for POS payment
      if (string.toLowerCase() === 'pos') {
        return 'POS payment';
      }
      string = string.replace(/_/g, ' ');
      return string.charAt(0).toUpperCase() + string.slice(1);
    },

    // Tab switching
    switchTab(index) {
      this.activeTabIndex = index
      this.tabKey++
      this.currentPage = 1
      this.fetchPayments()
    },

    // FilterBar change: apply new values and (re)fetch. The first change is
    // FilterBar's initial saved-views resolution and drives the first load.
    onFiltersChanged(values) {
      this.filterValues = {
        transaction_id: values.transaction_id || '',
        customer: values.customer || '',
        amount: { ...(values.amount || { from: '', to: '' }) },
        payment_method: Array.isArray(values.payment_method) ? [...values.payment_method] : [],
        status: Array.isArray(values.status) ? [...values.status] : [],
        date_processed: { ...(values.date_processed || { mode: 'range', days: '', from: '', to: '' }) },
      }
      if (!this.didInitialFetch) {
        this.didInitialFetch = true
        this.fetchPayments()
      } else {
        // Applying a filter/view lands on page 1; reset BEFORE fetch so the offset
        // (built inside fetchPayments from currentPage) is never out of range.
        this.currentPage = 1
        this.fetchPayments()
      }
    },

    onPerPageChange(n) {
      this.perPage = n
      this.currentPage = 1
      this.fetchPayments()
    },

    // Authentication
    async getAuthToken() {
      try {
        const session = await Auth.currentSession()
        return session.idToken.jwtToken
      } catch (error) {
        console.error('Error getting auth token:', error)
        throw error
      }
    },

    // API calls
    async fetchPayments() {
      try {
        this.loading = true
        const token = await this.getAuthToken()
        const config = { headers: { Authorization: token } }

        // Build query parameters
        const params = this.buildQueryParams()
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/payments?${params}`

        const response = await axios.get(url, config)

        this.payments = response.data.payments || []
        this.totalRows = response.data.total_rows || 0

        this.updateUrlQuery()
      } catch (error) {
        console.error('Error fetching payments:', error)
        if (error.response?.status === 401) {
          swal({
            title: 'Error',
            html: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
            type: 'error',
          }).then(() => {
            this.$router.push('/')
          })
        } else {
          swal('Error!', 'Something went wrong', 'error')
        }
      } finally {
        this.loading = false
      }
    },

    async fetchPaymentMethods() {
      try {
        const token = await this.getAuthToken()
        const config = { headers: { Authorization: token } }
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/payment-methods`

        const response = await axios.get(url, config)
        if (response.data.payment_methods && response.data.payment_methods.length > 0) {
          // Map API response: create 'method' field as lowercase type to match payment records
          this.paymentMethods = response.data.payment_methods.map(pm => ({
            ...pm,
            method: this.getMethodKey(pm.name)
          }))
        }
      } catch (error) {
        console.error('Error fetching payment methods:', error)
        // Use default payment methods if API fails
      }
    },

    getMethodKey(name) {
      if (!name) return ''
      // Map display names to their corresponding type values
      const mapping = {
        'pos payment': 'pos',
        'card': 'card',
        'cash': 'cash',
        'gift card': 'gift_card',
        'customer credit': 'customer_credit'
      }
      const lowerName = name.toLowerCase()
      return mapping[lowerName] || lowerName.replace(/\s+/g, '_')
    },

    async fetchPaymentStatuses() {
      this.paymentStatuses = [
        { status: 'successful', name: 'Successful' },
        { status: 'failed', name: 'Failed' },
        { status: 'pending', name: 'Pending' }
      ]
    },

    // Build query parameters for API from the flat filter values.
    buildQueryParams() {
      const params = new URLSearchParams()

      params.append('count', this.perPage)
      params.append('offset', this.getOffset())
      params.append('tab', this.activeTabIndex)

      const f = this.filterValues

      if (f.transaction_id) params.append('transaction_id', f.transaction_id)
      if (f.customer) params.append('customer', f.customer)

      const amount = f.amount || {}
      if (amount.from !== '' && amount.from != null) params.append('amount_from', amount.from)
      if (amount.to !== '' && amount.to != null) params.append('amount_to', amount.to)

      const { from, to } = this.computeDateProcessedRange()
      if (from) params.append('date_processed_from', from)
      if (to) params.append('date_processed_to', to)

      if (Array.isArray(f.payment_method) && f.payment_method.length > 0) {
        params.append('payment_method', f.payment_method.join(','))
      }
      if (Array.isArray(f.status) && f.status.length > 0) {
        params.append('status', f.status.join(','))
      }

      return params.toString()
    },

    // Pagination
    handlePageChange() {
      this.fetchPayments()
    },

    getOffset() {
      return (this.currentPage * this.perPage) - this.perPage
    },

    // CSV Export
    async downloadCSV() {
      try {
        this.loading = true
        const allPayments = await this.getAllPayments()

        const headers = [
          'Transaction ID',
          'Amount',
          'Payment Method',
          'Status',
          'Customer Name',
          'Customer Email',
          'Customer Phone',
          'Date Processed',
        ]

        const csvData = allPayments.map(payment => [
          payment.order_number,
          this.formatAmount(payment.amount),
          this.capitalizeFirstLetter(payment.type),
          this.capitalizeFirstLetter(payment.status),
          `${payment.customer_first_name} ${payment.customer_last_name}`,
          payment.customer_email,
          payment.customer_phone,
          this.formatDateTime(payment.processed_at)
        ])

        this.downloadCSVFile(csvData, headers, 'payments.csv')
      } catch (error) {
        console.error('Error exporting payments:', error)
        swal('Error!', 'Failed to export payments', 'error')
      } finally {
        this.loading = false
      }
    },

    async getAllPayments() {
      const token = await this.getAuthToken()
      const config = { headers: { Authorization: token } }
      const allPayments = []
      let offset = 0
      const count = 1000

      const { from, to } = this.computeDateProcessedRange()
      const f = this.filterValues

      while (offset <= this.totalRows) {
        const params = new URLSearchParams()
        params.append('count', count)
        params.append('offset', offset)
        params.append('tab', this.activeTabIndex)

        // Add current filters
        if (f.transaction_id) params.append('transaction_id', f.transaction_id)
        if (f.customer) params.append('customer', f.customer)
        if (f.amount && f.amount.from !== '' && f.amount.from != null) params.append('amount_from', f.amount.from)
        if (f.amount && f.amount.to !== '' && f.amount.to != null) params.append('amount_to', f.amount.to)
        if (from) params.append('date_processed_from', from)
        if (to) params.append('date_processed_to', to)
        if (Array.isArray(f.payment_method) && f.payment_method.length > 0) {
          params.append('payment_method', f.payment_method.join(','))
        }
        if (Array.isArray(f.status) && f.status.length > 0) {
          params.append('status', f.status.join(','))
        }

        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/payments?${params.toString()}`
        const response = await axios.get(url, config)

        allPayments.push(...(response.data.payments || []))
        offset += count
      }

      return allPayments
    },

    // CSV download utility
    downloadCSVFile(data, headers, filename) {
      let csv = headers.join(',') + '\n'

      data.forEach(row => {
        csv += row.map(value => {
          if (typeof value === 'string') {
            value = value.replace(/"/g, '""')
            if (value.includes(',') || value.includes('\n')) {
              value = `"${value}"`
            }
          }
          return value || ''
        }).join(',') + '\n'
      })

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)

      link.setAttribute('href', url)
      link.setAttribute('download', filename)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    },

    // URL management
    updateUrlQuery() {
      const d = this.filterValues.date_processed || {}
      const amount = this.filterValues.amount || {}
      const query = {
        count: this.perPage,
        page: this.currentPage,
        tab: this.activeTabIndex
      }

      // Preserve onboarding params
      if (this.$route.query.onboarding) query.onboarding = this.$route.query.onboarding
      if (this.$route.query.categoryId) query.categoryId = this.$route.query.categoryId
      if (this.$route.query.taskId) query.taskId = this.$route.query.taskId

      // Add filter parameters
      if (this.filterValues.transaction_id) query.transaction_id = this.filterValues.transaction_id
      if (this.filterValues.customer) query.customer = this.filterValues.customer
      if (amount.from !== '' && amount.from != null) query.amount_from = amount.from
      if (amount.to !== '' && amount.to != null) query.amount_to = amount.to
      if (d.mode === 'range' && d.from) query.date_processed_from = d.from
      if (d.mode === 'range' && d.to) query.date_processed_to = d.to
      if (Array.isArray(this.filterValues.payment_method) && this.filterValues.payment_method.length > 0) {
        query.payment_method = this.filterValues.payment_method.join(',')
      }
      if (Array.isArray(this.filterValues.status) && this.filterValues.status.length > 0) {
        query.status = this.filterValues.status.join(',')
      }

      this.$router.replace({ query }).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          throw err
        }
      })
    },

    // Utility methods
    hasPermissions(scope) {
      const permissions = JSON.parse(localStorage.getItem('permissions') || '[]')
      return permissions.includes(scope)
    },
    formatAmount(amount) {
      if (amount == null || isNaN(amount)) {
        // Handle null/NaN case with proper symbol positioning
        const currencyInfo = _.find(this.currencies, { code: this.currency });
        const isPostfix = currencyInfo ? currencyInfo.postfix : false;

        if (isPostfix) {
          return '0.00' + this.currencySymbol;  // 0.00kr
        } else {
          return this.currencySymbol + '0.00';  // $0.00
        }
      }

      const numericAmount = parseFloat(amount);

      // Get currency info for postfix check
      const currencyInfo = _.find(this.currencies, { code: this.currency });
      const isPostfix = currencyInfo ? currencyInfo.postfix : false;

      if (this.numberFormat === 1 && this.currencyFormat === 0) {
        // European formatting with symbol
        const formattedAmount = numericAmount.toLocaleString('de-DE', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });

        if (isPostfix) {
          return formattedAmount + this.currencySymbol;  // 12,34kr
        } else {
          return this.currencySymbol + formattedAmount;  // $12,34
        }
      } else if (this.numberFormat === 1 && this.currencyFormat === 1) {
        // European formatting with currency code
        return numericAmount.toLocaleString('de-DE', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }) + ' ' + this.currency;  // 12,34 SEK
      }

      // Default US formatting with symbol
      const formattedAmount = numericAmount.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });

      if (isPostfix) {
        return formattedAmount + this.currencySymbol;  // 12.34kr
      } else {
        return this.currencySymbol + formattedAmount;  // $12.34
      }
    },

    normalizeDateString(dateStr) {
      if (!dateStr || typeof dateStr !== 'string') return dateStr
      if (dateStr.includes('T') && dateStr.endsWith('Z')) return dateStr
      if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dateStr)) {
        return dateStr.replace(' ', 'T') + '.000Z'
      }
      return dateStr
    },
    formatDateTime(dateTime) {
      const d = formatGroupDate(dateTime, { dayOfWeek: false })
      const t = formatGroupTime(dateTime)
      if (!d && !t) return ''
      return `${d}, ${t}`
    },

    isPaymentDateEditable(item) {
      return item.type !== 'card' && item.type !== 'gift_card'
        && (item.status === 'completed' || item.status === 'successful')
    },
    initEditPaymentDate(payment, index) {
      const tz = JSON.parse(localStorage.getItem('group')).tz_name
      const m = tz ? moment.utc(payment.processed_at).tz(tz) : moment(payment.processed_at)
      const year = m.year()
      const month = String(m.month() + 1).padStart(2, '0')
      const day = String(m.date()).padStart(2, '0')
      const wallClock = new Date(m.year(), m.month(), m.date(), m.hours(), m.minutes())
      this.$set(this.editPaymentDateDate, index, `${year}-${month}-${day}`)
      this.$set(this.editPaymentDateTime, index, wallClock)
      this.$set(this.editPaymentDateError, index, '')
    },
    async updatePaymentDate(payment, index) {
      this.editPaymentDateLoading = true
      try {
        let token = await this.getAuthToken()
        let config = { headers: { Authorization: token } }
        let url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/payments/${payment.id}`

        let datePart = this.editPaymentDateDate[index]
        let timePart = this.editPaymentDateTime[index]
        let hours = String(timePart.getHours()).padStart(2, '0')
        let minutes = String(timePart.getMinutes()).padStart(2, '0')
        const tz = JSON.parse(localStorage.getItem('group')).tz_name
        const localStr = `${datePart} ${hours}:${minutes}:00`
        let timestamp = (tz ? moment.tz(localStr, tz) : moment(localStr)).utc().format('YYYY-MM-DD HH:mm:ss')

        await axios.put(url, { timestamp: timestamp }, config)

        payment.processed_at = timestamp
        this.$set(this.showEditPaymentDate, index, false)
        this.$notify({
          type: 'success',
          message: 'Payment date updated',
          icon: 'tim-icons icon-check-2'
        })
      } catch (e) {
        let message = 'Something went wrong. Please try again.'
        if (e.response && e.response.data) {
          message = e.response.data
        }
        this.$set(this.editPaymentDateError, index, message)
      } finally {
        this.editPaymentDateLoading = false
      }
    },
    getPaymentMethodBadgeType(method) {
      const types = {
        // 'card': 'primary',
        // 'gift_card': 'info',
        // 'Bank Transfer': 'success',
        // '': 'primary',
        // 'pos': 'primary',
        // 'cash': 'warning'
      }
      return types[method] || 'default'
    },

    getStatusBadgeType(status) {
      const types = {
        'completed': 'success',
        'successful': 'success',
        'pending': 'warning',
        'processing': 'warning',
        'failed': 'danger',
        'declined': 'danger',
        'cancelled': 'default',
        'refunded': 'info'
      }
      return types[status?.toLowerCase()] || 'default'
    }
  }
}
</script>

<style scoped>
.pagination-select {
  min-width: 80px;
}

.font-icon-detail {
  text-align: center;
  padding: 2rem;
}

.btn-simple {
  transition: all 0.3s ease;
}

.btn-simple:hover {
  transform: translateY(-1px);
}

.popover-header {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #333;
}

.popover-body {
  min-width: 250px;
}

.text-dark {
  color: #333 !important;
}

.text-dark label {
  font-weight: 500;
  margin-bottom: 0.5rem;
  display: block;
}

.payment-card {
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.5rem;
  background-color: rgba(255, 255, 255, 0.05);
  margin-bottom: 0.5rem;
}

.amount-display {
  font-size: 1.1rem;
  font-weight: 600;
  color: #28a745;
}

.customer-info {
  padding-left: 0.5rem;
  border-left: 2px solid rgba(255, 255, 255, 0.2);
}

.border-top {
  border-top: 1px solid rgba(255, 255, 255, 0.1) !important;
}

@media (max-width: 576px) {
  .d-flex.justify-content-between.flex-wrap > * {
    margin-bottom: 0.5rem;
  }

  .payment-card {
    padding: 0.75rem;
    margin-bottom: 0.75rem;
  }

  .amount-display {
    font-size: 1rem;
  }
}

.edit-date-btn {
  width: 24px;
  height: 24px;
  background: rgba(225, 78, 202, 0.15);
  border: none;
  border-radius: 5px;
  color: #e14eca;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  vertical-align: middle;
  transition: all 0.15s ease;
}
.edit-date-btn:hover {
  background: rgba(225, 78, 202, 0.25);
}
</style>
